import { useCallback, useEffect, useMemo, useState } from 'react';
import db, { type Cliente, type Material, type Config, type Cotacao } from '@/db';
import { fetchDailyExchangeRates, isRateFromToday, isSameLocalDay } from '@/services/exchangeRates';
import { supabase } from '@/services/supabaseClient';
import { useAuth } from '@/hooks/useAuth';
import { COTACOES_CHANGED_EVENT, listCotacoes, syncLocalCotacoes } from '@/services/cotacoesStore';

const PAGE_SIZE = 1000;

/** PostgREST devolve no máximo ~1000 linhas por consulta — pagina até acabar. */
async function fetchAllRows<T>(build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await build(from, from + PAGE_SIZE - 1);
    if (error) { console.error('Erro ao buscar dados do Supabase:', error); break; }
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
  }
  return all;
}

interface ClienteRowDb {
  codigo: string; nome: string; uf: string | null; cidade: string | null; alerta: string | null;
  condicao_pagamento: string | null; prazo: number | null; frete: string | null; cnpj: string | null;
  ponto_focal_nome: string | null; ponto_focal_email: string | null; ponto_focal_telefone: string | null;
  endereco_empresa: string | null; responsavel: string | null; divisao: string | null;
  frete_manual?: string | null; ativo?: boolean | null;
}

function mapCliente(row: ClienteRowDb, index: number): Cliente {
  return {
    id: index + 1,
    ativo: row.ativo !== false,
    codigo: row.codigo,
    nome: row.nome,
    uf: row.uf ?? '',
    cidade: row.cidade ?? '',
    alerta: row.alerta ?? '',
    condicaoPagamento: row.condicao_pagamento ?? '',
    prazo: row.prazo,
    frete: row.frete ?? '',
    freteManual: row.frete_manual ?? '',
    cnpj: row.cnpj ?? undefined,
    pontoFocalNome: row.ponto_focal_nome ?? undefined,
    pontoFocalEmail: row.ponto_focal_email ?? undefined,
    pontoFocalTelefone: row.ponto_focal_telefone ?? undefined,
    enderecoEmpresa: row.endereco_empresa ?? undefined,
    responsavel: row.responsavel ?? undefined,
    divisao: row.divisao ?? undefined,
  };
}

export async function fetchClienteByCodigo(codigo: string): Promise<Cliente | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from('zsb_clientes').select('*').eq('codigo', codigo).maybeSingle();
  if (error || !data) return null;
  return mapCliente(data as ClienteRowDb, 0);
}

export function useClientes(search?: string) {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!supabase) { setClientes([]); setLoading(false); return; }
    setLoading(true);
    const s = search?.trim();
    fetchAllRows<ClienteRowDb>((from, to) => {
      let query = supabase.from('zsb_clientes').select('*').order('codigo').range(from, to);
      if (s) query = query.or(`codigo.ilike.%${s}%,nome.ilike.%${s}%,uf.ilike.%${s}%,cidade.ilike.%${s}%`);
      return query;
    }).then((rows) => {
      setClientes(rows.map(mapCliente));
      setLoading(false);
    });
  }, [search]);

  useEffect(() => { reload(); }, [reload]);

  // `clientes` só traz clientes ativos (únicos que podem receber cotação). Telas de
  // cadastro e análise histórica usam `todosClientes`.
  const ativos = useMemo(() => clientes.filter((c) => c.ativo !== false), [clientes]);
  return { clientes: ativos, todosClientes: clientes, loading, reload };
}

interface MaterialRowDb {
  codigo: string; descricao: string; origem_fiscal: string | null; codigo_origem: string | null;
  custo_reposicao: number | null; ipi: number | null; icms_rs: number | null; unidade_medida: string | null;
  responsaveis: string[] | null; divisoes: string[] | null; estoque?: number | null; ativo?: boolean | null; conta_gerencial?: string | null;
}

function mapMaterial(row: MaterialRowDb, index: number): Material {
  return {
    id: index + 1,
    codigo: row.codigo,
    descricao: row.descricao,
    origemFiscal: row.origem_fiscal ?? '',
    custoReposicao: row.custo_reposicao ?? 0,
    ipi: row.ipi ?? 0,
    icmsRs: row.icms_rs ?? 0,
    codigoOrigem: row.codigo_origem ?? '',
    estoque: row.estoque ?? null,
    unidadeMedida: row.unidade_medida || 'KG',
    responsaveis: row.responsaveis ?? [],
    divisoes: row.divisoes ?? [],
    ativo: row.ativo !== false,
    contaGerencial: row.conta_gerencial ?? undefined,
  };
}

export function useMateriais(search?: string) {
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!supabase) { setMateriais([]); setLoading(false); return; }
    setLoading(true);
    const s = search?.trim();
    fetchAllRows<MaterialRowDb>((from, to) => {
      let query = supabase.from('zsb_materiais').select('*').order('codigo').range(from, to);
      if (s) query = query.or(`codigo.ilike.%${s}%,descricao.ilike.%${s}%`);
      return query;
    }).then((rows) => {
      setMateriais(rows.map(mapMaterial));
      setLoading(false);
    });
  }, [search]);

  useEffect(() => { reload(); }, [reload]);

  // `materiais` só traz produtos ativos (os únicos que podem ser usados em cotação,
  // amostra ou projeto). Telas de cadastro/análise usam `todosMateriais`.
  const ativos = useMemo(() => materiais.filter((m) => m.ativo !== false), [materiais]);
  return { materiais: ativos, todosMateriais: materiais, loading, reload };
}

export interface PrecoTabela {
  drawback: boolean;
  clienteCodigo: string;
  empresa: string;
  materialCodigo: string;
  produto: string;
  responsavel: string;
  divisao: string;
  precoComImpostos: number | null;
  precoNet: number;
  moeda: string;
  ultimaModificacao: string;
  estado: string;
  cidade: string;
  icms: number;
  ipi: number;
  pisCofins: number;
  fator: number;
}

interface PrecoRowDb {
  drawback?: boolean;
  cliente_codigo: string; empresa: string | null; material_codigo: string; produto: string;
  responsavel: string | null; divisao: string | null; preco_com_impostos: number | null; preco_net: number;
  moeda: string; ultima_modificacao: string | null; estado: string | null; cidade: string | null;
  icms: number | null; ipi: number | null; pis_cofins: number | null; fator: number | null;
}

function mapPreco(row: PrecoRowDb): PrecoTabela {
  return {
    drawback: row.drawback ?? false,
    clienteCodigo: row.cliente_codigo,
    empresa: row.empresa ?? '',
    materialCodigo: row.material_codigo,
    produto: row.produto,
    responsavel: row.responsavel ?? '',
    divisao: row.divisao ?? '',
    precoComImpostos: row.preco_com_impostos,
    precoNet: row.preco_net,
    moeda: row.moeda,
    ultimaModificacao: row.ultima_modificacao ?? '',
    estado: row.estado ?? '',
    cidade: row.cidade ?? '',
    icms: row.icms ?? 0,
    ipi: row.ipi ?? 0,
    pisCofins: row.pis_cofins ?? 0,
    fator: row.fator ?? 0,
  };
}

export function usePrecos() {
  const [precos, setPrecos] = useState<PrecoTabela[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!supabase) { setPrecos([]); setLoading(false); return; }
    setLoading(true);
    fetchAllRows<PrecoRowDb>((from, to) => supabase.from('zsb_precos').select('*').range(from, to))
      .then((rows) => {
        setPrecos(rows.map(mapPreco));
        setLoading(false);
      });
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { precos, loading, reload };
}

export interface VendaLedger {
  numeroNf: string;
  serieNf: string;
  dataEmissao: string;
  codDivisao: string;
  divisao: string;
  codCliente: string;
  cliente: string;
  codMaterial: string;
  material: string;
  faturamento: number;
  qtde: number;
}

interface VendaRowDb {
  numero_nf: string; serie_nf: string | null; data_emissao: string; cod_divisao: string | null;
  divisao: string | null; cod_cliente: string; cliente: string | null; cod_material: string;
  material: string | null; faturamento: number | null; qtde: number | null;
}

function mapVenda(row: VendaRowDb): VendaLedger {
  return {
    numeroNf: row.numero_nf,
    serieNf: row.serie_nf ?? '',
    dataEmissao: row.data_emissao,
    codDivisao: row.cod_divisao ?? '',
    divisao: row.divisao ?? '',
    codCliente: row.cod_cliente,
    cliente: row.cliente ?? '',
    codMaterial: row.cod_material,
    material: row.material ?? '',
    faturamento: row.faturamento ?? 0,
    qtde: row.qtde ?? 0,
  };
}

export function useVendasLedger() {
  const [vendas, setVendas] = useState<VendaLedger[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!supabase) { setVendas([]); setLoading(false); return; }
    setLoading(true);
    fetchAllRows<VendaRowDb>((from, to) =>
      supabase.from('zsb_vendas_ledger').select('numero_nf,serie_nf,data_emissao,cod_divisao,divisao,cod_cliente,cliente,cod_material,material,faturamento,qtde').range(from, to))
      .then((rows) => {
        setVendas(rows.map(mapVenda));
        setLoading(false);
      });
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { vendas, loading, reload };
}

export function useConfig() {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    let active = true;
    const loadAndRefresh = async () => {
      const current = await db.config.get(1);
      if (current && active) setConfig(current);
      // Já atualizado hoje com o fechamento do dia anterior? Valor de hoje (regra antiga) é refeito.
      if (current && isSameLocalDay(current.cambioAtualizadoEm) && current.cambioDataFonte && !isRateFromToday(current.cambioDataFonte)) return;
      try {
        const rates = await fetchDailyExchangeRates();
        const updated: Partial<Config> = {
          cotacaoUsd: rates.usd,
          cotacaoEur: rates.eur,
          cambioAtualizadoEm: new Date().toISOString(),
          cambioDataFonte: rates.sourceDate,
        };
        await db.config.update(1, updated);
        const refreshed = await db.config.get(1);
        if (refreshed && active) setConfig(refreshed);
      } catch (error) {
        console.warn('Não foi possível atualizar as cotações PTAX; mantendo os últimos valores válidos.', error);
      }
    };
    void loadAndRefresh();
    const interval = window.setInterval(loadAndRefresh, 60 * 60 * 1000);
    return () => { active = false; window.clearInterval(interval); };
  }, []);

  const updateConfig = async (data: Partial<Config>) => {
    await db.config.update(1, data);
    const updated = await db.config.get(1);
    if (updated) setConfig(updated);
  };

  return { config, updateConfig };
}

export function useCotacoes() {
  const { user } = useAuth();
  const [cotacoes, setCotacoes] = useState<Cotacao[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    listCotacoes()
      .then((data) => setCotacoes(data))
      .catch((error) => console.error('Erro ao carregar cotações:', error))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
    window.addEventListener(COTACOES_CHANGED_EVENT, reload);
    return () => window.removeEventListener(COTACOES_CHANGED_EVENT, reload);
  }, [reload]);

  // Cotações antigas (ou salvas sem conexão) que estão só neste navegador sobem para a nuvem.
  useEffect(() => { if (user) void syncLocalCotacoes(user); }, [user]);

  return { cotacoes, loading, reload };
}
